import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClientCreateSchema,
  ClientUpdateSchema,
  type ClientCreateRequest,
  type ClientDetail,
  type ClientUpdateRequest,
} from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useClientCreate, useClientUpdate } from './api';

type Props = {
  open: boolean;
  client: ClientDetail | null;
  onClose: () => void;
  onCreated?: (id: number) => void;
};

type Form = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  marketingConsent: boolean;
  rodoConsent: boolean;
};

const EMPTY: Form = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  marketingConsent: false,
  rodoConsent: false,
};

function fromClient(c: ClientDetail): Form {
  return {
    firstName: c.firstName,
    lastName: c.lastName ?? '',
    phone: c.phone,
    email: c.email ?? '',
    marketingConsent: c.marketingConsent,
    rodoConsent: c.rodoConsent,
  };
}

export function ClientFormModal({ open, client, onClose, onCreated }: Props): ReactNode {
  const { t } = useTranslation();
  const isEdit = client !== null;

  const create = useClientCreate();
  const update = useClientUpdate(client?.id ?? 0);

  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    setForm(client ? fromClient(client) : EMPTY);
  }, [open, client]);

  if (!open) return null;

  const validate = (): ClientCreateRequest | ClientUpdateRequest | null => {
    const candidate: Record<string, unknown> = {
      firstName: form.firstName,
      lastName: form.lastName.trim() === '' ? null : form.lastName,
      phone: form.phone,
      email: form.email.trim() === '' ? null : form.email,
      marketingConsent: form.marketingConsent,
      rodoConsent: form.rodoConsent,
    };
    const schema = isEdit ? ClientUpdateSchema : ClientCreateSchema;
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const fld: Partial<Record<keyof Form, string>> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (
          path === 'firstName' ||
          path === 'lastName' ||
          path === 'phone' ||
          path === 'email'
        ) {
          fld[path] = issue.message;
        }
      }
      setErrors(fld);
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const parsed = validate();
    if (!parsed) return;
    try {
      if (isEdit && client) {
        await update.mutateAsync(parsed as ClientUpdateRequest);
      } else {
        const created = await create.mutateAsync(parsed as ClientCreateRequest);
        onCreated?.(created.id);
      }
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err));
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {isEdit ? t('clients.form.titleEdit') : t('clients.form.titleCreate')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('clients.form.firstName')} error={errors.firstName}>
              <input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                className="input"
                required
                autoFocus
              />
            </Field>
            <Field label={t('clients.form.lastName')} error={errors.lastName}>
              <input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                className="input"
              />
            </Field>
          </div>

          <Field
            label={t('clients.form.phone')}
            error={errors.phone}
            hint={t('clients.form.phoneHint')}
          >
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="input"
              autoComplete="tel"
              required
              placeholder="+48 512 345 678"
            />
          </Field>

          <Field label={t('clients.form.email')} error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="input"
              autoComplete="email"
            />
          </Field>

          <div className="space-y-2 rounded-lg border border-zinc-200 p-3">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.rodoConsent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rodoConsent: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="flex-1">
                <span className="font-medium text-zinc-800">
                  {t('clients.form.rodoConsent')}
                </span>
                <span className="block text-xs text-zinc-500">
                  {t('clients.form.rodoConsentHint')}
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.marketingConsent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, marketingConsent: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="flex-1">
                <span className="font-medium text-zinc-800">
                  {t('clients.form.marketingConsent')}
                </span>
                <span className="block text-xs text-zinc-500">
                  {t('clients.form.marketingConsentHint')}
                </span>
              </span>
            </label>
          </div>

          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-700">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && !error && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
